import { Info, Notifications, Settings } from "@mui/icons-material";
import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Switch,
  Typography,
  debounce,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AppSelect, InputBase, InputLabel } from "components/common/Input";
import usePickup from "hooks/pickups/usePickup";
import useUpdatePickup from "hooks/pickups/useUpdatePickup";
import theme from "lib/theme";
import { format, parseISO, startOfDay } from "date-fns";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Pickup } from "types/pickup";
import { useMissingRequirement } from "./requirementError";

interface PickupReasonProps {
  step: number;
  goToNextStep: () => void;
  registerPendingSave?: (savePickupType: () => Promise<boolean>) => void;
}

const BASIC_PICKUP_TYPE_OPTIONS = ["Standard device retiring/refresh", "Office Closure", "Site Decommission"];

const PICKUP_TYPE_OPTIONS = [...BASIC_PICKUP_TYPE_OPTIONS, "Other"];

type PickupType = (typeof PICKUP_TYPE_OPTIONS)[number];

const LEGACY_REASON_MAP: Record<string, PickupType> = {
  "Office closure": "Office Closure",
};

const twoColumnGridSx = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 3,
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
};

const conditionalSectionSx = {
  mt: 3,
  p: 2.5,
  borderRadius: 1,
  border: "1px solid rgba(0, 137, 189, 0.25)",
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  backgroundColor: "rgba(0, 137, 189, 0.04)",
};

interface PickupReasonFormState {
  reason: string;
  devicesCentralizedLocation: boolean;
  devicesAlreadyPalletized: boolean;
  onSitePersonPresent: boolean;
  officeClosureDate: Date | null;
  otherVendorsOnSite: string;
  devicesLeftBehind: string;
}

const DEFAULT_FORM_STATE: PickupReasonFormState = {
  reason: "",
  devicesCentralizedLocation: true,
  devicesAlreadyPalletized: false,
  onSitePersonPresent: true,
  officeClosureDate: null,
  otherVendorsOnSite: "",
  devicesLeftBehind: "",
};

const parseDateField = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  try {
    return parseISO(value);
  } catch {
    return null;
  }
};

const normalizeReason = (reason?: string): string => {
  if (!reason) {
    return "";
  }

  if (BASIC_PICKUP_TYPE_OPTIONS.includes(reason)) {
    return reason;
  }

  return LEGACY_REASON_MAP[reason] ?? reason;
};

const isOtherReason = (reason: string) => !!reason && !BASIC_PICKUP_TYPE_OPTIONS.includes(reason);

const formStateFromPickup = (pickup: Pickup): PickupReasonFormState => ({
  reason: normalizeReason(pickup.reason),
  devicesCentralizedLocation: pickup.devicesCentralizedLocation ?? true,
  devicesAlreadyPalletized: pickup.devicesAlreadyPalletized ?? false,
  onSitePersonPresent: pickup.onSitePersonPresent ?? true,
  officeClosureDate: parseDateField(pickup.officeClosureDate),
  otherVendorsOnSite: pickup.otherVendorsOnSite ?? "",
  devicesLeftBehind: pickup.devicesLeftBehind ?? "",
});

const formatDateForApi = (date: Date | null): string | null => {
  if (!date) {
    return null;
  }

  return format(date, "yyyy-MM-dd");
};

const buildUpdatePayload = (state: PickupReasonFormState) => ({
  reason: state.reason || undefined,
  devicesCentralizedLocation: state.devicesCentralizedLocation,
  devicesAlreadyPalletized: state.devicesAlreadyPalletized,
  onSitePersonPresent: state.onSitePersonPresent,
  officeClosureDate: formatDateForApi(state.officeClosureDate),
  otherVendorsOnSite: state.otherVendorsOnSite,
  devicesLeftBehind: state.devicesLeftBehind,
});

const OFFICE_CLOSURE_FIELD_DEFAULTS: Pick<
  PickupReasonFormState,
  "onSitePersonPresent" | "officeClosureDate" | "otherVendorsOnSite" | "devicesLeftBehind"
> = {
  onSitePersonPresent: DEFAULT_FORM_STATE.onSitePersonPresent,
  officeClosureDate: DEFAULT_FORM_STATE.officeClosureDate,
  otherVendorsOnSite: DEFAULT_FORM_STATE.otherVendorsOnSite,
  devicesLeftBehind: DEFAULT_FORM_STATE.devicesLeftBehind,
};

const clearFieldsForPickupType = (reason: string): Partial<PickupReasonFormState> => {
  if (reason === "Office Closure") {
    return {};
  }

  return OFFICE_CLOSURE_FIELD_DEFAULTS;
};

interface ToggleFieldProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ToggleField = ({ label, description, checked, onChange }: ToggleFieldProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
      }}
    >
      <Box>
        <Typography fontWeight={600}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Switch checked={checked} color="primary" onChange={(event) => onChange(event.target.checked)} />
    </Box>
  );
};

interface SectionHeaderProps {
  icon: ReactNode;
  title: string;
  description?: string;
}

const SectionHeader = ({ icon, title, description }: SectionHeaderProps) => {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            display: "flex",
            alignItems: "center",
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Box>
        <Typography fontWeight={700} fontSize="18px">
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography mt={0.5} variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
  );
};

interface ConditionalSectionHeaderProps {
  title: string;
}

const ConditionalSectionHeader = ({ title }: ConditionalSectionHeaderProps) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Info sx={{ fontSize: 18, color: theme.palette.primary.main }} />
      <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {title}
      </Typography>
    </Box>
  );
};

const HeadsUpAlert = ({ children, showPrefix = true }: { children: ReactNode; showPrefix?: boolean }) => (
  <Alert
    severity="warning"
    icon={<Notifications sx={{ color: theme.palette.warning.main }} />}
    sx={{
      mb: 2,
      backgroundColor: "#FEF3C7",
      border: `1px solid ${theme.palette.warning.main}`,
      borderLeft: `4px solid ${theme.palette.secondary.dark}`,
      borderRadius: 1,
      color: "#78350F",
      ".MuiAlert-message": { width: "100%" },
    }}
  >
    {showPrefix && (
      <Typography variant="body2" component="span" fontWeight={700}>
        Heads up:{" "}
      </Typography>
    )}
    <Typography variant="body2" component="span">
      {children}
    </Typography>
  </Alert>
);

interface DateFieldProps {
  label: string;
  value: Date | null;
  description?: string;
  formatString?: string;
  onChange: (value: Date | null) => void;
}

const DateField = ({ label, value, description, formatString = "EEEE, MMMM d", onChange }: DateFieldProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <DatePicker
        open={pickerOpen}
        onOpen={() => setPickerOpen(true)}
        onClose={() => setPickerOpen(false)}
        value={value}
        onChange={onChange}
        format={formatString}
        minDate={startOfDay(new Date())}
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          actionBar: {
            actions: ["clear"],
          },
          textField: {
            onClick: () => setPickerOpen(true),
            sx: {
              cursor: "pointer",
              ".MuiOutlinedInput-root": {
                overflow: "hidden",
                paddingRight: "4px !important",
              },
              ".MuiInputAdornment-positionEnd": {
                marginLeft: 0,
              },
              ".MuiInputAdornment-positionEnd .MuiIconButton-root": {
                padding: "6px",
                marginRight: "2px",
              },
            },
            inputProps: {
              readOnly: true,
              placeholder: "Select a date",
            },
          },
        }}
        slots={{
          textField: InputBase,
        }}
      />
      {description && (
        <Typography mt={0.5} variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </FormControl>
  );
};

interface TextFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  description?: string;
  onChange: (value: string) => void;
}

const TextField = ({ label, value, placeholder, description, onChange }: TextFieldProps) => {
  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <InputBase value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      {description && (
        <Typography mt={0.5} variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </FormControl>
  );
};

interface TextAreaFieldProps {
  label: string;
  value: string;
  placeholder: string;
  description?: string;
  onChange: (value: string) => void;
}

const TextAreaField = ({ label, value, placeholder, description, onChange }: TextAreaFieldProps) => {
  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <InputBase
        multiline
        minRows={3}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      {description && (
        <Typography mt={0.5} variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </FormControl>
  );
};

export default function PickupReason({ step, goToNextStep, registerPendingSave }: PickupReasonProps) {
  const { pickup, isLoading } = usePickup();
  const { updatePickup } = useUpdatePickup();
  const [formState, setFormState] = useState<PickupReasonFormState>(DEFAULT_FORM_STATE);
  const [initialStateLoaded, setInitialStateLoaded] = useState(false);
  const [showOtherReasonInput, setShowOtherReasonInput] = useState(false);

  const debouncedSavePickup = useMemo(
    () =>
      debounce((payload: ReturnType<typeof buildUpdatePayload>) => {
        updatePickup(payload);
      }, 500),
    [updatePickup],
  );

  useEffect(() => {
    return () => {
      debouncedSavePickup.clear();
    };
  }, [debouncedSavePickup]);

  const savePickupType = useCallback(async () => {
    if (!initialStateLoaded) return true;

    debouncedSavePickup.clear();

    try {
      await updatePickup(buildUpdatePayload(formState));
      return true;
    } catch {
      return false;
    }
  }, [debouncedSavePickup, formState, initialStateLoaded, updatePickup]);

  registerPendingSave?.(savePickupType);

  useEffect(() => {
    if (!pickup || isLoading) {
      return;
    }

    const nextState = formStateFromPickup(pickup);
    setFormState(nextState);
    setShowOtherReasonInput(isOtherReason(nextState.reason));
    setInitialStateLoaded(true);
  }, [pickup, isLoading]);

  useEffect(() => {
    if (!initialStateLoaded || !pickup) {
      return;
    }

    debouncedSavePickup(buildUpdatePayload(formState));
  }, [formState, initialStateLoaded, pickup, debouncedSavePickup]);

  const updateFormField = (updates: Partial<PickupReasonFormState>) => {
    setFormState((current) => ({ ...current, ...updates }));
  };

  const handlePickupTypeChange = (reason: string) => {
    const nextState = {
      ...formState,
      reason,
      ...clearFieldsForPickupType(reason),
    };

    setFormState(nextState);
    debouncedSavePickup.clear();
    updatePickup(buildUpdatePayload(nextState));

    if (BASIC_PICKUP_TYPE_OPTIONS.includes(reason)) {
      setShowOtherReasonInput(false);
      goToNextStep();
    } else {
      setShowOtherReasonInput(true);
      setFormState((current) => ({ ...current, reason: "" }));
    }
  };

  const isOfficeClosure = formState.reason === "Office Closure";
  const isSiteDecommission = formState.reason === "Site Decommission";
  const typeMissing = useMissingRequirement("schedule-pickup-type") && !formState.reason.trim();

  return (
    <Box>
      <Typography variant="h6" fontSize="18px" fontWeight="bold">
        {step}. Why are you requesting a pickup?
      </Typography>

      <FormControl
        id="schedule-pickup-type"
        error={typeMissing}
        variant="standard"
        sx={{ mt: 3, width: "100%", maxWidth: "450px" }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.5, display: "block" }}
        >
          Pickup type
        </Typography>
        <AppSelect
          error={typeMissing && !showOtherReasonInput}
          value={isOtherReason(formState.reason) || showOtherReasonInput ? "Other" : formState.reason}
          placeholder="Select a pickup type"
          onChange={(event) => handlePickupTypeChange(event.target.value as string)}
        >
          {BASIC_PICKUP_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          <MenuItem key="Other" value="Other">
            Other
          </MenuItem>
        </AppSelect>

        {typeMissing && !showOtherReasonInput && <FormHelperText>Required</FormHelperText>}
        {showOtherReasonInput && (
          <InputBase
            error={typeMissing}
            helperText={typeMissing ? "Required" : undefined}
            value={formState.reason}
            placeholder="Enter the reason for this pickup"
            sx={{ mt: 1 }}
            onChange={(event) => updateFormField({ reason: event.target.value })}
            onBlur={() => goToNextStep()}
          />
        )}
      </FormControl>

      {isOfficeClosure && (
        <Box sx={conditionalSectionSx}>
          <ConditionalSectionHeader title="Office Closure - Additional Details" />
          <HeadsUpAlert>
            We recommend submitting this request at least 7 days before your closure date so we can confirm vendor
            availability and coordinate the pickup.
          </HeadsUpAlert>

          <ToggleField
            label="An on-site person will be present"
            description="Helpful for crew check-in and last-minute changes."
            checked={formState.onSitePersonPresent}
            onChange={(checked) => updateFormField({ onSitePersonPresent: checked })}
          />

          <Box sx={{ ...twoColumnGridSx, mt: 2 }}>
            <DateField
              label="Actual office closure date"
              value={formState.officeClosureDate}
              formatString="MMMM d, yyyy"
              description="The date your office officially shuts down or your lease ends."
              onChange={(date) => updateFormField({ officeClosureDate: date })}
            />
            <TextField
              label="Other vendors on site that day?"
              value={formState.otherVendorsOnSite}
              placeholder="e.g. Org_1: furniture removal, 2 PM"
              description="So crews can coordinate timing."
              onChange={(value) => updateFormField({ otherVendorsOnSite: value })}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextAreaField
              label="Devices being left behind?"
              value={formState.devicesLeftBehind}
              placeholder="Enter details here if applicable."
              description="e.g. monitors mounted to walls, fixtures the building owner keeps."
              onChange={(value) => updateFormField({ devicesLeftBehind: value })}
            />
          </Box>
        </Box>
      )}

      {isSiteDecommission && (
        <Box sx={conditionalSectionSx}>
          <ConditionalSectionHeader title="Site Decommission - Coordination Required" />
          <HeadsUpAlert showPrefix={false}>
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                A Revivn rep will be in touch within 24 hours of submission.
              </Box>{" "}
              Decommissions involve physically removing things from the building (server racks, mounted TVs, etc.), so
              we coordinate the specifics with you 1:1. For now, please provide a device list and your preferred dates
              below – that&apos;s all we need to get started.
            </>
          </HeadsUpAlert>
        </Box>
      )}

      {formState.reason && (
        <>
          <Divider sx={{ mt: 4, mb: 3 }} />

          <SectionHeader
            icon={<Settings sx={{ fontSize: 18 }} />}
            title="Logistics Preferences"
            description="A few quick details that help the crew prepare. All pickups now include a standard 15-minute advance call from the driver."
          />

          <Box sx={{ mt: 2 }}>
            <ToggleField
              label="Devices are in a centralized location"
              description="Saves time over collecting from multiple floors or rooms."
              checked={formState.devicesCentralizedLocation}
              onChange={(checked) => updateFormField({ devicesCentralizedLocation: checked })}
            />
            <Divider />
            <ToggleField
              label="Devices are already palletized"
              description="If off, we'll bring pallets and shrink-wrap."
              checked={formState.devicesAlreadyPalletized}
              onChange={(checked) => updateFormField({ devicesAlreadyPalletized: checked })}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
